import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { MemberAvatar } from "../components/MemberFormPieces";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  CalendarCheck, Clock, Plus, Trash, CheckCircle, XCircle,
  UmbrellaSimple, Confetti, ArrowRight, Warning, DownloadSimple, FileCsv, FilePdf, UsersThree, PencilSimple,
} from "@phosphor-icons/react";
import { API } from "../lib/api";
import { GeoCell, OfficeLocationPanel, selfieUrl } from "../components/AttendanceGeo";
import { MissedCheckinsCard } from "../components/MissedCheckins";
import { ManualAttendanceDialog } from "../components/AttendanceManual";

const TABS = [
  { key: "summary", label: "Attendance Summary" },
  { key: "leaves", label: "Leaves" },
  { key: "holidays", label: "Holidays" },
  { key: "office", label: "Office Location", adminOnly: true },
];

const LEAVE_TYPES = [
  { key: "casual", label: "Casual" },
  { key: "sick", label: "Sick" },
  { key: "earned", label: "Earned" },
];

function isoMonthNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AttendancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "manager";
  const [tab, setTab] = useState("summary");

  return (
    <Layout
      title="Attendance"
      subtitle={isAdmin
        ? "Track every staff member's daily attendance, review leave requests, and manage the holiday calendar."
        : "Track your daily attendance, request leaves, and view the studio holiday calendar."}
    >
      <div className="flex items-center gap-0 border border-zinc-300 bg-white mb-6 w-fit flex-wrap" data-testid="attendance-tabs">
        {TABS.filter((t) => !t.adminOnly || isAdmin).map((t) => (
          <button
            key={t.key} data-testid={`att-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 sm:px-5 py-2.5 label-overline border-r border-zinc-300 last:border-r-0 ${
              tab === t.key ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "summary" && <SummaryPanel isAdmin={isAdmin} isManagerOrAdmin={isManagerOrAdmin} me={user} />}
      {tab === "leaves" && <LeavesPanel isManagerOrAdmin={isManagerOrAdmin} me={user} />}
      {tab === "holidays" && <HolidaysPanel isAdmin={isAdmin} />}
      {tab === "office" && isAdmin && <OfficeLocationPanel />}
    </Layout>
  );
}

/* ---------------- Summary ---------------- */

function SummaryPanel({ isAdmin, isManagerOrAdmin, me }) {
  const [month, setMonth] = useState(isoMonthNow());
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(me?.id || "");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [manual, setManual] = useState(null); // null | { user_id?, date? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const targetUser = isAdmin ? (selectedUser || me?.id) : me?.id;
      const uPromise = isAdmin ? http.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] });
      const [uRes, sumRes, rowsRes] = await Promise.all([
        uPromise,
        http.get(`/attendance/summary?month=${month}${isAdmin && targetUser ? `&user_id=${targetUser}` : ""}`),
        http.get(`/attendance?from_date=${month}-01&to_date=${month}-31${isAdmin && targetUser ? `&user_id=${targetUser}` : ""}`),
      ]);
      setUsers(uRes.data.filter((u) => u.role !== "client"));
      setSummary(sumRes.data);
      setRows(rowsRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [month, isAdmin, selectedUser, me?.id]);

  useEffect(() => { load(); }, [load]);

  const downloadReport = async (format, allStaff = false) => {
    setDownloading(true);
    try {
      const uid = allStaff ? "all" : (isAdmin ? (selectedUser || me?.id) : me?.id);
      const path = format === "html"
        ? `/attendance/report/html?month=${month}&user_id=${uid}`
        : `/attendance/report/csv?month=${month}&user_id=${uid}`;
      if (format === "html") {
        // Open printable HTML in new tab
        const url = `${API}${path}`;
        const { data } = await http.get(path, { responseType: "blob" });
        const blob = new Blob([data], { type: "text/html" });
        const objUrl = URL.createObjectURL(blob);
        const w = window.open(objUrl, "_blank");
        if (!w) toast.error("Popup blocked — allow popups for this site to open the printable report");
        setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
      } else {
        // CSV — force download
        const { data, headers } = await http.get(path, { responseType: "blob" });
        const disposition = headers["content-disposition"] || "";
        const match = /filename="?([^"]+)"?/.exec(disposition);
        const filename = match ? match[1] : `attendance-${month}.csv`;
        const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
      toast.success("Report generated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-5" data-testid="summary-toolbar">
        <div>
          <label className="label-overline block mb-1 text-zinc-700">Month</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 data-testid="summary-month"
                 className="rounded-none border-zinc-400 w-44 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
        </div>
        {isAdmin && (
          <div className="min-w-[240px]">
            <label className="label-overline block mb-1 text-zinc-700">Staff Member</label>
            <Select value={selectedUser || me?.id} onValueChange={setSelectedUser}>
              <SelectTrigger data-testid="summary-user" className="rounded-none border-zinc-400">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id} className="rounded-none">
                    {u.name} · {u.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="sm:ml-auto flex items-end gap-2 flex-wrap">
          {isAdmin && (
            <Button
              type="button" onClick={() => setManual({ user_id: selectedUser || me?.id })}
              data-testid="manual-mark-btn"
              className="rounded-none h-10 bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs px-4"
            >
              <PencilSimple size={12} weight="bold" className="mr-1.5" /> Manual Mark
            </Button>
          )}
          <Button
            type="button" onClick={() => downloadReport("csv")} disabled={downloading}
            data-testid="download-csv-btn"
            className="rounded-none h-10 bg-emerald-700 hover:bg-emerald-800 text-white font-mono uppercase tracking-widest text-xs px-4"
          >
            <FileCsv size={12} weight="bold" className="mr-1.5" />
            {downloading ? "…" : "CSV"}
          </Button>
          <Button
            type="button" onClick={() => downloadReport("html")} disabled={downloading}
            data-testid="download-pdf-btn"
            className="rounded-none h-10 bg-red-700 hover:bg-red-800 text-white font-mono uppercase tracking-widest text-xs px-4"
          >
            <FilePdf size={12} weight="bold" className="mr-1.5" />
            {downloading ? "…" : "PDF"}
          </Button>
          {isAdmin && (
            <Button
              type="button" onClick={() => downloadReport("csv", true)} disabled={downloading}
              data-testid="download-all-staff-btn"
              className="rounded-none h-10 bg-zinc-900 hover:bg-blue-600 text-white font-mono uppercase tracking-widest text-xs px-4"
            >
              <UsersThree size={12} weight="bold" className="mr-1.5" />
              All Staff CSV
            </Button>
          )}
        </div>
      </div>

      {isManagerOrAdmin && <MissedCheckinsCard onManualMark={isAdmin ? (uid) => setManual({ user_id: uid }) : null} />}

      {loading && <div className="label-overline text-zinc-500">Loading…</div>}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-300 bg-white mb-6" data-testid="summary-stats">
          <Stat label="Working Days" value={summary.total_days - summary.sundays - summary.holidays} />
          <Stat label="Present (Full)" value={summary.present_full} accent="text-emerald-700" />
          <Stat label="Half-Day" value={summary.present_half} accent="text-blue-700" />
          <Stat label="Absent" value={summary.absent} accent="text-red-600" />
          <Stat label="Leaves Taken" value={summary.leaves} accent="text-orange-700" />
          <Stat label="Sundays" value={summary.sundays} />
          <Stat label="Holidays" value={summary.holidays} />
          <Stat label="Total Hours" value={summary.present_full * 8 + summary.present_half * 4} accent="text-zinc-900" />
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-zinc-300 bg-white overflow-x-auto" data-testid="attendance-rows">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 border-b border-zinc-300">
              <tr>
                <th className="px-4 py-2 text-left label-overline text-zinc-700 whitespace-nowrap">Date</th>
                {isAdmin && <th className="px-4 py-2 text-left label-overline text-zinc-700 whitespace-nowrap">Name</th>}
                <th className="px-4 py-2 text-left label-overline text-zinc-700">First Half</th>
                <th className="px-4 py-2 text-left label-overline text-zinc-700">Second Half</th>
                {isAdmin && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`attendance-row-${r.id}`} className="border-b border-zinc-200 last:border-b-0">
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{r.date}</td>
                  {isAdmin && <td className="px-4 py-2 text-zinc-800">{r.user_name || r.user_id}</td>}
                  <td className="px-4 py-2 align-top">
                    <AttMark v={r.first_half} />
                    <ManualChip m={r.first_half_manual} />
                    {isManagerOrAdmin && <GeoCell geo={r.first_half_geo} hasSelfie={r.first_half_selfie} selfieUrl={selfieUrl(r.id, "first")} />}
                  </td>
                  <td className="px-4 py-2 align-top">
                    <AttMark v={r.second_half} />
                    <ManualChip m={r.second_half_manual} />
                    {isManagerOrAdmin && <GeoCell geo={r.second_half_geo} hasSelfie={r.second_half_selfie} selfieUrl={selfieUrl(r.id, "second")} />}
                  </td>
                  {isAdmin && (
                    <td className="px-2 py-2 align-top text-right">
                      <button type="button" onClick={() => setManual({ user_id: r.user_id, date: r.date })}
                              data-testid={`edit-attendance-${r.id}`} title="Manual mark"
                              className="text-zinc-400 hover:text-orange-600 p-1">
                        <PencilSimple size={14} weight="bold" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && (
        <ManualAttendanceDialog
          open={!!manual} onOpenChange={(v) => !v && setManual(null)}
          users={users} initial={manual} onSaved={load}
        />
      )}
    </div>
  );
}

function ManualChip({ m }) {
  if (!m) return null;
  return (
    <div className="mt-1 label-overline text-orange-700 flex items-center gap-1" title={m.note || ""} data-testid="manual-chip">
      <PencilSimple size={10} weight="bold" /> Manual · {m.by}
    </div>
  );
}

function AttMark({ v }) {
  if (v === "present") return <span className="label-overline bg-emerald-100 text-emerald-800 px-2 py-1">Present</span>;
  if (v === "absent") return <span className="label-overline bg-red-100 text-red-700 px-2 py-1">Absent</span>;
  return <span className="label-overline bg-zinc-100 text-zinc-500 px-2 py-1">—</span>;
}

function Stat({ label, value, accent = "text-zinc-900" }) {
  return (
    <div className="p-4 border-r border-b md:border-b-0 border-zinc-300 last:border-r-0">
      <div className="label-overline text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-black tracking-tighter mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

/* ---------------- Leaves ---------------- */

function LeavesPanel({ isManagerOrAdmin, me }) {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [decideOn, setDecideOn] = useState(null); // { row, action }
  const [pendingDelete, setPendingDelete] = useState(null);
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    try {
      const url = tab === "all" ? "/leaves" : `/leaves?status=${tab}`;
      const { data } = await http.get(url);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action, note) => {
    try {
      await http.patch(`/leaves/${id}/${action}`, { admin_note: note || "" });
      toast.success(action === "approve" ? "Leave approved" : "Leave rejected");
      setDecideOn(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await http.delete(`/leaves/${pendingDelete.id}`);
      toast.success("Leave withdrawn");
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="leaves-toolbar">
        <div className="flex items-center gap-0 border border-zinc-300 bg-white w-fit">
          {["pending", "approved", "rejected", "all"].map((k) => (
            <button
              key={k} data-testid={`leaves-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 label-overline border-r border-zinc-300 last:border-r-0 ${
                tab === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >{k}</button>
          ))}
        </div>
        <Button
          onClick={() => setCreating(true)} data-testid="new-leave-btn"
          className="rounded-none h-10 bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs px-4 ml-auto"
        >
          <Plus size={12} weight="bold" className="mr-1.5" /> Request Leave
        </Button>
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-10 text-center bg-white">
          <UmbrellaSimple size={32} weight="bold" className="mx-auto text-zinc-300" />
          <div className="label-overline text-zinc-500 mt-3">No {tab === "all" ? "" : tab} leaves</div>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((lv) => (
          <div key={lv.id} data-testid={`leave-${lv.id}`}
               className="border border-zinc-300 bg-white p-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="min-w-[120px]">
              <div className="label-overline text-zinc-500">{lv.leave_type}</div>
              <div className="font-mono text-xs mt-1">{lv.from_date} → {lv.to_date}</div>
            </div>
            <div className="flex-1 min-w-0">
              {isManagerOrAdmin && (
                <div className="text-sm font-semibold text-zinc-900">{lv.user_name || lv.user_id}</div>
              )}
              <div className="text-sm text-zinc-700 mt-0.5">{lv.reason}</div>
              {lv.admin_note && (
                <div className="text-xs text-zinc-500 mt-1">Note: {lv.admin_note}</div>
              )}
            </div>
            <div className="flex items-center gap-2 md:ml-auto">
              <StatusPill status={lv.status} />
              {isManagerOrAdmin && lv.status === "pending" && (
                <>
                  <button
                    onClick={() => setDecideOn({ row: lv, action: "approve" })}
                    data-testid={`approve-leave-${lv.id}`}
                    className="label-overline bg-emerald-100 text-emerald-800 hover:bg-emerald-700 hover:text-white px-2 py-1 flex items-center gap-1"
                  ><CheckCircle size={11} weight="bold" /> Approve</button>
                  <button
                    onClick={() => setDecideOn({ row: lv, action: "reject" })}
                    data-testid={`reject-leave-${lv.id}`}
                    className="label-overline bg-red-100 text-red-700 hover:bg-red-600 hover:text-white px-2 py-1 flex items-center gap-1"
                  ><XCircle size={11} weight="bold" /> Reject</button>
                </>
              )}
              {(lv.user_id === me?.id && lv.status === "pending") && (
                <button onClick={() => setPendingDelete(lv)}
                        data-testid={`withdraw-leave-${lv.id}`}
                        className="text-zinc-400 hover:text-red-600 p-1">
                  <Trash size={13} weight="bold" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <LeaveCreateDialog open={creating} onOpenChange={setCreating} onCreated={load} />
      <LeaveDecideDialog
        state={decideOn}
        onOpenChange={(v) => !v && setDecideOn(null)}
        onSubmit={(note) => decide(decideOn.row.id, decideOn.action, note)}
      />
      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Withdraw leave request?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              {pendingDelete?.from_date} → {pendingDelete?.to_date}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-withdraw-leave"
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs">
              Yes, Withdraw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-700",
  };
  return <span className={`label-overline px-2 py-1 ${map[status] || "bg-zinc-200"}`}>{status}</span>;
}

function LeaveCreateDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ from_date: "", to_date: "", leave_type: "casual", reason: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ from_date: "", to_date: "", leave_type: "casual", reason: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.from_date || !form.to_date) { toast.error("Please pick both dates"); return; }
    if (!form.reason.trim()) { toast.error("Reason is required"); return; }
    setSaving(true);
    try {
      await http.post("/leaves", form);
      toast.success("Leave requested");
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-lg">
        <DialogHeader>
          <div className="label-overline text-orange-600 mb-1">Leave Request</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            Request Leave
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="leave-create-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-1 text-zinc-700">From *</label>
              <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                     required data-testid="leave-from"
                     className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
            </div>
            <div>
              <label className="label-overline block mb-1 text-zinc-700">To *</label>
              <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                     required data-testid="leave-to"
                     className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
            </div>
          </div>
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Type *</label>
            <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
              <SelectTrigger data-testid="leave-type" className="rounded-none border-zinc-400">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {LEAVE_TYPES.map((t) => (
                  <SelectItem key={t.key} value={t.key} className="rounded-none">{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Reason *</label>
            <Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      required data-testid="leave-reason"
                      className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="submit-leave"
                    className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs">
              {saving ? "Requesting…" : "Submit Request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LeaveDecideDialog({ state, onOpenChange, onSubmit }) {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [state]);
  if (!state) return null;
  const isApprove = state.action === "approve";
  return (
    <Dialog open={!!state} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            {isApprove ? "Approve Leave" : "Reject Leave"}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-zinc-700 my-2">
          <strong>{state.row.user_name}</strong> — {state.row.from_date} → {state.row.to_date}
          <div className="mt-1 text-zinc-600">{state.row.reason}</div>
        </div>
        <Textarea placeholder="Optional note to the requester" rows={3} value={note}
                  onChange={(e) => setNote(e.target.value)} data-testid="decide-note"
                  className="rounded-none border-zinc-400" />
        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300 mt-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                  className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">
            Cancel
          </Button>
          <Button type="button" onClick={() => onSubmit(note)} data-testid={`confirm-${state.action}`}
                  className={`rounded-none text-white font-mono uppercase tracking-widest text-xs ${
                    isApprove ? "bg-emerald-700 hover:bg-emerald-800" : "bg-red-600 hover:bg-red-700"
                  }`}>
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Holidays ---------------- */

function HolidaysPanel({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/holidays?year=${year}`);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await http.delete(`/holidays/${pendingDelete.date}`);
      toast.success("Holiday removed");
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="holidays-toolbar">
        <div>
          <label className="label-overline block mb-1 text-zinc-700">Year</label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)}
                 data-testid="holiday-year"
                 className="rounded-none border-zinc-400 w-32 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
        </div>
        {isAdmin && (
          <Button
            onClick={() => setCreating(true)} data-testid="new-holiday-btn"
            className="rounded-none h-10 bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs px-4 ml-auto self-end"
          >
            <Plus size={12} weight="bold" className="mr-1.5" /> Add Holiday
          </Button>
        )}
      </div>

      <div className="text-xs text-zinc-500 font-mono mb-3 flex items-center gap-1.5">
        <Warning size={11} weight="bold" /> Every Sunday is auto-marked as a holiday.
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-10 text-center bg-white">
          <Confetti size={32} weight="bold" className="mx-auto text-zinc-300" />
          <div className="label-overline text-zinc-500 mt-3">No holidays configured for {year}</div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((h) => (
          <div key={h.date} data-testid={`holiday-${h.date}`}
               className="border-t-2 border-orange-500 border-x border-b border-zinc-300 bg-white p-4">
            <div className="label-overline text-orange-600">{new Date(h.date).toLocaleDateString(undefined, {weekday:"short"})}</div>
            <div className="font-display text-xl font-black tracking-tighter mt-1">{h.date}</div>
            <div className="text-sm text-zinc-800 mt-1">{h.label}</div>
            {isAdmin && (
              <button onClick={() => setPendingDelete(h)} data-testid={`delete-holiday-${h.date}`}
                      className="mt-3 label-overline text-zinc-500 hover:text-red-600 flex items-center gap-1">
                <Trash size={11} weight="bold" /> Remove
              </button>
            )}
          </div>
        ))}
      </div>

      <HolidayCreateDialog open={creating} onOpenChange={setCreating} onCreated={load} />

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Remove {pendingDelete?.label}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will remove <strong>{pendingDelete?.date}</strong> from the studio holiday list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-holiday"
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs">
              Yes, Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HolidayCreateDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ date: "", label: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ date: "", label: "" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.label.trim()) { toast.error("Date and label required"); return; }
    setSaving(true);
    try {
      await http.post("/holidays", form);
      toast.success("Holiday added");
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-md">
        <DialogHeader>
          <div className="label-overline text-orange-600 mb-1">Studio Calendar</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            Add Holiday
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="holiday-create-form">
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Date *</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                   required data-testid="holiday-date"
                   className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
          </div>
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Label *</label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                   required placeholder="e.g. Independence Day"
                   data-testid="holiday-label"
                   className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="submit-holiday"
                    className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs">
              {saving ? "Adding…" : "Add Holiday"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
